import { useEffect, useRef, useState } from 'react';

interface SpaceShooterProps {
  onGameEnd: (winner: string | null) => void;
  isHost: boolean;
  sendGameState: (state: any) => void;
  gameId?: string;
  isOffline?: boolean;
}

interface GameObject {
  x: number;
  y: number;
  width: number;
  height: number;
  speed?: number;
}

const CANVAS_WIDTH = 600;
const CANVAS_HEIGHT = 800;

const SpaceShooter = ({ onGameEnd, isHost, sendGameState }: SpaceShooterProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);

  // All mutable game state in refs to avoid stale closures in the game loop
  const playerRef = useRef<GameObject>({
    x: CANVAS_WIDTH / 2,
    y: isHost ? CANVAS_HEIGHT - 50 : 50,
    width: 40,
    height: 40,
    speed: 5,
  });
  const opponentRef = useRef<GameObject>({
    x: CANVAS_WIDTH / 2,
    y: isHost ? 50 : CANVAS_HEIGHT - 50,
    width: 40,
    height: 40,
  });
  const bulletsRef = useRef<GameObject[]>([]);
  const enemiesRef = useRef<GameObject[]>([]);
  const scoreRef = useRef(0);
  const gameOverRef = useRef(false);
  const keysRef = useRef<Record<string, boolean>>({});
  const lastShotRef = useRef(0);
  const lastSpawnRef = useRef(0);

  // Key handling — write directly to ref, no re-renders on every keypress
  useEffect(() => {
    const down = (e: KeyboardEvent) => { keysRef.current[e.key] = true; };
    const up = (e: KeyboardEvent) => { delete keysRef.current[e.key]; };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, []);

  // Receive opponent position + enemy sync from Firebase
  useEffect(() => {
    const handleGameState = (e: CustomEvent) => {
      const state = e.detail;
      if (state.type === 'ss_pos') {
        opponentRef.current = { ...opponentRef.current, x: state.x, y: state.y };
      }
      // Guest receives authoritative enemy list from host each frame
      if (!isHost && state.type === 'ss_enemies') {
        enemiesRef.current = state.enemies;
      }
    };
    window.addEventListener('game_state', handleGameState as EventListener);
    return () => window.removeEventListener('game_state', handleGameState as EventListener);
  }, [isHost]);

  // Stable game loop — runs once, all state accessed via refs
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const loop = setInterval(() => {
      if (gameOverRef.current) return;

      const player = playerRef.current;
      const now = Date.now();

      // Movement
      if (keysRef.current['ArrowLeft'] && player.x > 0) player.x -= player.speed!;
      if (keysRef.current['ArrowRight'] && player.x < CANVAS_WIDTH - player.width) player.x += player.speed!;

      // Shoot (200ms cooldown, max 5 bullets)
      if (keysRef.current[' '] && bulletsRef.current.length < 5 && now - lastShotRef.current > 200) {
        lastShotRef.current = now;
        bulletsRef.current.push({
          x: player.x + player.width / 2 - 2,
          y: isHost ? player.y - 10 : player.y + player.height,
          width: 4,
          height: 10,
          speed: 7,
        });
      }

      // Move bullets
      bulletsRef.current = bulletsRef.current
        .map(b => ({ ...b, y: isHost ? b.y - b.speed! : b.y + b.speed! }))
        .filter(b => b.y > 0 && b.y < CANVAS_HEIGHT);

      // Host: spawn + move enemies, then sync to guest
      if (isHost) {
        if (now - lastSpawnRef.current > 800 && Math.random() < 0.5) {
          lastSpawnRef.current = now;
          enemiesRef.current.push({
            x: Math.random() * (CANVAS_WIDTH - 30),
            y: 0,
            width: 30,
            height: 30,
            speed: 2,
          });
        }

        enemiesRef.current = enemiesRef.current.map(e => ({ ...e, y: e.y + e.speed! }));

        // Enemy escaped — game over
        if (enemiesRef.current.some(e => e.y > CANVAS_HEIGHT)) {
          enemiesRef.current = enemiesRef.current.filter(e => e.y <= CANVAS_HEIGHT);
          gameOverRef.current = true;
          setGameOver(true);
          onGameEnd(null);
          return;
        }

        sendGameState({ type: 'ss_enemies', enemies: enemiesRef.current });
      }

      // Both players: bullet–enemy collision (each player scores independently)
      const hitEnemies = new Set<number>();
      const hitBullets = new Set<number>();
      enemiesRef.current.forEach((enemy, ei) => {
        bulletsRef.current.forEach((bullet, bi) => {
          if (checkCollision(bullet, enemy)) {
            hitEnemies.add(ei);
            hitBullets.add(bi);
            scoreRef.current += 10;
            setScore(scoreRef.current);
          }
        });
      });
      enemiesRef.current = enemiesRef.current.filter((_, i) => !hitEnemies.has(i));
      bulletsRef.current = bulletsRef.current.filter((_, i) => !hitBullets.has(i));

      // Send position to opponent (position only, not full state)
      sendGameState({ type: 'ss_pos', x: player.x, y: player.y });

      // --- Render ---
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      // Player ship (green)
      ctx.fillStyle = '#00ff00';
      drawShip(ctx, player.x, player.y, player.width, player.height, isHost);

      // Opponent ship (red)
      const opp = opponentRef.current;
      ctx.fillStyle = '#ff4444';
      drawShip(ctx, opp.x, opp.y, opp.width, opp.height, !isHost);

      // Bullets (white)
      ctx.fillStyle = '#fff';
      bulletsRef.current.forEach(b => ctx.fillRect(b.x, b.y, b.width, b.height));

      // Enemies (magenta)
      ctx.fillStyle = '#ff00ff';
      enemiesRef.current.forEach(e => ctx.fillRect(e.x, e.y, e.width, e.height));

      // Score overlay
      ctx.fillStyle = '#fff';
      ctx.font = '18px monospace';
      ctx.textAlign = 'left';
      ctx.fillText(`Score: ${scoreRef.current}`, 10, 24);

      // Player labels
      ctx.font = '12px sans-serif';
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.textAlign = 'center';
      ctx.fillText('You', player.x + player.width / 2, isHost ? player.y - 8 : player.y + player.height + 16);
      ctx.fillText('Opponent', opp.x + opp.width / 2, isHost ? opp.y - 8 : opp.y + opp.height + 16);

    }, 1000 / 60);

    return () => clearInterval(loop);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally stable — all state accessed via refs

  return (
    <div className="flex flex-col items-center">
      <div className="mb-4">
        <span className="text-xl font-bold">Score: {score}</span>
      </div>
      <canvas
        ref={canvasRef}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        className="border border-gray-700 rounded-lg"
      />
      <div className="mt-4 text-sm text-gray-300">
        Use ← → to move, SPACE to shoot · Green = you, Red = opponent
      </div>
      {gameOver && <div className="mt-4 text-red-400 font-bold text-xl">Game Over!</div>}
    </div>
  );
};

function drawShip(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, pointingUp: boolean) {
  ctx.beginPath();
  if (pointingUp) {
    ctx.moveTo(x + w / 2, y);
    ctx.lineTo(x + w, y + h);
    ctx.lineTo(x, y + h);
  } else {
    ctx.moveTo(x + w / 2, y + h);
    ctx.lineTo(x + w, y);
    ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fill();
}

function checkCollision(a: GameObject, b: GameObject): boolean {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
}

export default SpaceShooter;
